import React from 'react';
import { getTemplate } from '../../utils/printerUtils';
import { Tag, Space, Tooltip, message } from 'antd';
import axios from 'axios';
import { DeleteOutlined, PrinterOutlined, DownloadOutlined, CheckCircleFilled } from '@ant-design/icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Export generateLabel function to be reused by OrderList
export const generateLabel = async (order) => {
  try {
    // Create a temporary div for the shipping label
    const tempDiv = document.createElement('div');
    tempDiv.style.width = '100mm';
    tempDiv.style.height = '150mm';
    tempDiv.style.padding = '0';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.backgroundColor = 'white';
    tempDiv.style.boxSizing = 'border-box';

    // Get the template using printerUtils
    const port = order.port;
    const courier = order.courier_service;
    if (!port || !courier) {
      console.warn('Missing courier service');
      message.error('缺少快递服务信息');
      return null;
    }

    let template;
    try {
      template = getTemplate(courier, port);
    } catch (error) {
      console.error('Template selection failed:', error);
      message.error(error.message);
      return null;
    }
    
    // Generate label HTML using the template
    const labelHtml = await template(order);
    if (!labelHtml) {
      message.error('面单生成失败');
      return null;
    }
    
    tempDiv.innerHTML = labelHtml;
    document.body.appendChild(tempDiv);

    // Single shorter wait for rendering
    await new Promise(resolve => setTimeout(resolve, 300));

    // Capture the content as an image
    const canvas = await html2canvas(tempDiv, {
      scale: 4,
      useCORS: true,
      logging: false,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 378,
      height: 567,
      onclone: (clonedDoc) => {
        const clonedDiv = clonedDoc.querySelector('div');
        if (clonedDiv) {
          clonedDiv.style.width = '378px';
          clonedDiv.style.height = '567px';
        }
      }
    });

    document.body.removeChild(tempDiv);
    return canvas;
  } catch (error) {
    console.error('Print error:', error);
    message.error('面单生成失败');
    return null;
  }
};

const OrderListItem = ({
  item, 
  columnStyle, 
  onOrderClick, 
  formatDate, 
  getStatusColor,
  getStatusLabel,
  onSelect,
  isSelected,
  onOrdersUpdate,
  isStaff
}) => {

  const getCsrfToken = () => {
    const name = 'csrftoken=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let cookie of cookieArray) {
      cookie = cookie.trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length, cookie.length);
      }
    }
    return '';
  };

  const recordHistory = async (action) => {
    try {
      await axios.post(`/api/orders/${item.id}/history/`, {
        action: action
      });
    } catch (error) {
      console.error('Failed to record history:', error);
    }
  };

  const handlePrint = async (e) => {
    e.stopPropagation();
    message.loading({ content: '准备打印...', key: 'printLoading' });
    
    try {
      // Record print action in backend first
      await axios.post('/api/orders/batch_print/', {
        order_ids: [item.id]
      });
      await recordHistory('print');
      
      const canvas = await generateLabel(item);
      if (!canvas) return;

      // Create data URL and open in new window
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      const printWindow = window.open('', '_blank');
      
      if (!printWindow) {
        message.error({ content: '请允许弹出窗口以进行打印', key: 'printLoading' });
        return;
      }

      // Write minimal HTML content
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <style>
              @page { size: 100mm 150mm; margin: 0; }
              body { margin: 0; padding: 0; }
              img { width: 100mm; height: 150mm; display: block; }
            </style>
          </head>
          <body>
            <img src="${dataUrl}" onload="setTimeout(window.print, 10)" />
          </body>
        </html>
      `);
      printWindow.document.close();

      // Close window after printing
      printWindow.onafterprint = () => {
        printWindow.close();
        message.success({ content: '打印请求已发送', key: 'printLoading' });
      };
    } catch (error) {
      console.error('Print error:', error);
      message.error({ content: '打印失败', key: 'printLoading' });
    }
  };

  const handleSave = async (e) => {
    e.stopPropagation();
    message.loading({ content: '生成PDF...', key: 'pdfLoading' });
    
    try {
      // Record print action in backend first
      await axios.post('/api/orders/batch_print/', {
        order_ids: [item.id]
      });
      await recordHistory('save');
      
      const canvas = await generateLabel(item);
      if (!canvas) return;

      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [100, 150],
        compress: true,
        putOnlyUsedFonts: true,
        precision: 8
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, 100, 150, undefined, 'FAST');
      pdf.save(`shipping-label-${item.order_number}.pdf`);
      message.success({ content: 'PDF已保存', key: 'pdfLoading' });
    } catch (error) {
      console.error('PDF generation error:', error);
      message.error({ content: 'PDF生成失败', key: 'pdfLoading' });
    }
  };

  return (
    <>
      <div style={columnStyle.sequenceNumber}>
        {item.index + 1}
      </div>
      <div style={columnStyle.orderNumber}>
        <Space>
          {item.order_number}
          {item.is_deleted && (
            <Tooltip title="用户已删除">
              <DeleteOutlined style={{ color: '#ff4d4f', fontSize: '14px' }} />
            </Tooltip>
          )}
        </Space>
      </div>
      <div style={columnStyle.expressNumber}>{item.express_number}</div>
      <div style={columnStyle.recipientName}>{item.label_recipient || item.recipient_name}</div>
      <div style={columnStyle.recipientPhone}>{item.label_phone || item.recipient_phone || '-'}</div>
      <div style={columnStyle.senderName}>{item.sender_name || '-'}</div>
      <div style={columnStyle.senderPhone}>{item.sender_phone || '-'}</div>
      <div style={columnStyle.batchNumber}>{item.batch_number || '-'}</div>
      {isStaff && (
        <div style={columnStyle.customer}>{item.customer_name || '-'}</div>
      )}
      <div style={columnStyle.date}>{formatDate(item.created_at)}</div>
      <div style={columnStyle.printTimes}>{item.print_times || 0}</div>
      <div style={columnStyle.weight}>{item.weight_per_item || '-'}</div>
      <div style={columnStyle.status}>
        <Tag style={{ padding: '0 4px', fontSize: '12px', margin: 0 }} color={getStatusColor(item.status)}>
          {getStatusLabel(item.status)}
        </Tag>
      </div>
      <div style={columnStyle.inspection}>
        {item.is_inspection_needed && <CheckCircleFilled style={{ color: '#1890ff', fontSize: '16px' }} />}
      </div>
      <div style={columnStyle.shortPackage}>
        {item.is_short && <CheckCircleFilled style={{ color: '#1890ff', fontSize: '16px' }} />}
      </div>
      <div style={columnStyle.claim}>
        {item.is_claim && <CheckCircleFilled style={{ color: '#1890ff', fontSize: '16px' }} />}
      </div>
      <div style={columnStyle.modified}>
        {item.is_modified && <CheckCircleFilled style={{ color: '#1890ff', fontSize: '16px' }} />}
      </div>
      <div style={columnStyle.operation}>
        <Space size={8}>
          <PrinterOutlined 
            style={{ cursor: 'pointer', fontSize: '14px' }}
            onClick={handlePrint}
          />
          <DownloadOutlined
            style={{ cursor: 'pointer', fontSize: '14px' }}
            onClick={handleSave}
          />
        </Space>
      </div>
    </>
  );
};

export default OrderListItem;
